import { campaignBriefTextWorkflow } from "./definitions/campaign-brief-text";
import { contentValidationStep } from "./step-modules/content-validation";
import { languageClarityReviewStep } from "./step-modules/language-clarity-review";
import { voiceReviewStep } from "./step-modules/voice-review";
import { approvalGateStep } from "./step-modules/approval-gate";
import { ValidationStepRenderer } from "./renderers/ValidationStepRenderer";
import { ClarityStepRenderer } from "./renderers/ClarityStepRenderer";
import { VoiceReviewStepRenderer } from "./renderers/VoiceReviewStepRenderer";
import { ApprovalStepRenderer } from "./renderers/ApprovalStepRenderer";
import type {
  WorkflowDefinition,
  WorkflowStepModule,
  WorkflowStepRendererComponent,
} from "./types";

const defaultWorkflowDefinitions: Array<[string, WorkflowDefinition]> = [
  [campaignBriefTextWorkflow.id, campaignBriefTextWorkflow],
];

const defaultWorkflowSteps: Array<[string, WorkflowStepModule]> = [
  [contentValidationStep.id, contentValidationStep],
  [languageClarityReviewStep.id, languageClarityReviewStep],
  [voiceReviewStep.id, voiceReviewStep],
  [approvalGateStep.id, approvalGateStep],
];

// Each renderer declares its own snapshot type, but the registry stores them
// uniformly and the panel invokes them with a generic, type-erased run-state.
// `<any>` is the deliberate seam between per-renderer snapshot typing and the
// registry/panel boundary (the renderer narrows the snapshot internally).
const defaultWorkflowRenderers: Array<[string, WorkflowStepRendererComponent<any>]> = [
  ["validation", ValidationStepRenderer],
  ["clarity", ClarityStepRenderer],
  ["voice-review", VoiceReviewStepRenderer],
  ["approval", ApprovalStepRenderer],
];

const workflowDefinitions = new Map<string, WorkflowDefinition>(defaultWorkflowDefinitions);
const workflowSteps = new Map<string, WorkflowStepModule>(defaultWorkflowSteps);
const workflowRenderers = new Map<string, WorkflowStepRendererComponent<any>>(defaultWorkflowRenderers);

export function getWorkflowDefinition(id: string): WorkflowDefinition | null {
  return workflowDefinitions.get(id) ?? null;
}

export function getWorkflowStep(id: string): WorkflowStepModule | null {
  return workflowSteps.get(id) ?? null;
}

export function getWorkflowRenderer(id: string): WorkflowStepRendererComponent<any> | null {
  return workflowRenderers.get(id) ?? null;
}

export function registerWorkflowDefinition(definition: WorkflowDefinition): void {
  workflowDefinitions.set(definition.id, definition);
}

export function registerWorkflowStep(step: WorkflowStepModule): void {
  workflowSteps.set(step.id, step);
}

export function registerWorkflowRenderer(
  id: string,
  renderer: WorkflowStepRendererComponent<any>,
): void {
  workflowRenderers.set(id, renderer);
}

export function resetWorkflowRegistry(): void {
  workflowDefinitions.clear();
  workflowSteps.clear();
  workflowRenderers.clear();

  for (const [id, definition] of defaultWorkflowDefinitions) {
    workflowDefinitions.set(id, definition);
  }
  for (const [id, step] of defaultWorkflowSteps) {
    workflowSteps.set(id, step);
  }
  for (const [id, renderer] of defaultWorkflowRenderers) {
    workflowRenderers.set(id, renderer);
  }
}
